import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body2, Heading6 } from '@ui/Typography'
import { Divider } from '@ui/components/Divider'
import { Link } from '@ui/components/Link'
import { ListItem } from '@ui/components/List'
import { PageSection } from '@ui/components/PageSection'
import { SimpleList } from '@ui/components/SimpleList'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'

import { useGAEvents } from '@utils/callGAEvents'

import { useLinksConfig } from '@hooks/useLinksConfig'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { Box } from '@shared/components/Box'

export const SocialTradingPage = () => {
  useScrollToTop()

  const { t } = useTranslation()

  const { callGAEvents } = useGAEvents()
  const linksProps = useLinks()
  const { registrationURL, providerRegistrationURL, loginURL, exploreStrategiesLink } = linksProps
  const { userGuideLink } = linksProps

  return (
    <Box flex direction='column' gap={{ desktop: 'x32', mobile: 'x16' }}>
      <PageSection title={t('IB.SocialTradingPage.How to Get Started')} />
      <Box flex gap='x16' wrap>
        <InfoBox
          title={t('IB.SocialTradingPage.For Provider')}
          onClick={() => callGAEvents().SocialTradingPage.Links.userGuideProvider()}
          link={userGuideLink}
          points={[
            'IB.SocialTradingPage.Open a Trading Account with with a minimum balance',
            'IB.SocialTradingPage.Register as a provider and choose the trading account',
            'IB.SocialTradingPage.Select the performance fee you want to charge',
          ]}
        />
        <InfoBox
          title={t('IB.SocialTradingPage.For Follower')}
          onClick={() => callGAEvents().SocialTradingPage.Links.userGuideFollower()}
          link={userGuideLink}
          points={[
            'IB.SocialTradingPage.Create a Trading Account',
            'IB.SocialTradingPage.Register as a follower using the MT4/MT5 credentials',
            'IB.SocialTradingPage.Choose your favourite Strategy Providers and let them do the work',
          ]}
        />
      </Box>

      <PageSection title={t('IB.SocialTradingPage.Links')} />
      <Box flex direction='column' gap='x16'>
        <LinkItem
          href={loginURL}
          onClick={() => callGAEvents().SocialTradingPage.Links.login()}
          title={t('IB.SocialTradingPage.Login')}
        />
        <Divider.Horizontal />
        <LinkItem
          href={exploreStrategiesLink}
          onClick={() => callGAEvents().SocialTradingPage.Links.strategyProviders()}
          title={t('IB.SocialTradingPage.Explore Strategies')}
        />
        <Divider.Horizontal />
        <LinkItem
          href={registrationURL}
          onClick={() => callGAEvents().SocialTradingPage.Links.followerRegistration()}
          title={t('IB.SocialTradingPage.Follower Registration')}
        />
        <Divider.Horizontal />
        <LinkItem
          href={providerRegistrationURL}
          onClick={() => callGAEvents().SocialTradingPage.Links.providerRegistration()}
          title={t('IB.SocialTradingPage.Provider Registration')}
        />
      </Box>
    </Box>
  )
}

const useLinks = () => {
  const { links } = useLinksConfig()
  const locale = useSessionLanguage()

  const createURL = (baseUrl: string) => {
    return `${baseUrl}?lang=${locale}`
  }

  return useMemo(
    () => ({
      userGuideLink: links.socialTrading.UserGuide,
      exploreStrategiesLink: links.socialTrading.ExploreStrategies,
      loginURL: createURL(links.socialTrading.Login),
      registrationURL: createURL(links.socialTrading.FollowerRegistration),
      providerRegistrationURL: createURL(links.socialTrading.ProviderRegistration),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale]
  )
}

interface InfoBoxProps {
  title: string
  points: string[]
  onClick: () => void
  link: string
}

const InfoBox = ({ title, points, onClick, link }: InfoBoxProps) => (
  <Box
    direction='column'
    gap='x16'
    fullWidth
    radius='x16'
    p='x24'
    bg='surface-info-base-em'
    minWidth={400}
    flex={1}
  >
    <Heading6 semibold>{title}</Heading6>
    <SimpleList>
      {points.map((point, index) => (
        <Body2 as='span' color='text-med-em' key={index}>
          <li>
            <Trans
              i18nKey={point}
              components={{
                a: (
                  <Link
                    variant='tertiary'
                    href={link}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={onClick}
                  />
                ),
              }}
            />
          </li>
        </Body2>
      ))}
    </SimpleList>
  </Box>
)

interface LinkItemProps {
  href: string
  title: string
  onClick: () => void
}

const LinkItem: React.FC<LinkItemProps> = ({ title, href, onClick }) => (
  <ListItem
    size='lg'
    link={{ href }}
    onClick={onClick}
    title={title}
    endSlot={{
      icon: { icon: { icon: ArrowCircleIcon.UpRight, color: 'text-med-em' }, size: 'x32' },
    }}
  />
)
